import { defineStore } from 'pinia';
import { authService, type User } from '../services/authService';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null,
    token: null as string | null,
    isLoading: false,
    error: null as string | null,
    isInitialized: false,
    isCheckingAuth: false,
    lastActivity: null as number | null,
    /** Jeton court après mot de passe OK, en attente du code TOTP */
    pendingTwoFactorToken: null as string | null
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    currentUser: (state) => state.user,
    isAdmin: (state) => state.user?.role === 'admin',
    isManager: (state) => state.user?.role === 'manager' || state.user?.role === 'admin',
    tokenExpiry: (state) => {
      if (!state.token) return null;
      try {
        const payload = JSON.parse(atob(state.token.split('.')[1]));
        return payload.exp * 1000; // Convertir en millisecondes
      } catch {
        return null;
      }
    },
    isTokenExpired(): boolean {
      const expMs = this.tokenExpiry;
      if (expMs === null) return true;
      return Date.now() >= expMs;
    }
  },
  
  actions: {
    // Initialiser l'authentification depuis le localStorage
    initializeFromStorage() {
      try {
        console.log('🔍 Tentative de restauration depuis le localStorage...');
        
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user-data');
        const lastActivity = localStorage.getItem('last-activity');
        
        console.log('🔍 Données trouvées:', { 
          hasToken: !!token, 
          hasUserData: !!userData, 
          hasLastActivity: !!lastActivity 
        });
        
        if (token && userData) {
          try {
            const user = JSON.parse(userData);
            const activity = lastActivity ? parseInt(lastActivity) : null;
            
            console.log('🔍 Utilisateur parsé:', { 
              userId: user._id || user.id, 
              email: user.email, 
              role: user.role 
            });
            
            // Vérifier si le token n'est pas expiré directement depuis le token
            const isExpired = this.checkTokenExpiration(token);
            if (!isExpired) {
              this.token = token;
              this.user = user;
              this.lastActivity = activity;
              this.updateLastActivity();
              console.log('✅ Authentification restaurée depuis le localStorage');
              return true;
            } else {
              console.log('⚠️ Token expiré, nettoyage du localStorage');
              this.clearStorage();
            }
          } catch (parseError) {
            console.error('❌ Erreur de parsing des données utilisateur:', parseError);
            this.clearStorage();
          }
        } else {
          console.log('🔍 Aucune donnée d\'authentification trouvée dans le localStorage');
        }
        return false;
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation depuis le localStorage:', error);
        this.clearStorage();
        return false;
      }
    },

    // Sauvegarder l'état dans le localStorage
    saveToStorage() {
      if (this.token && this.user) {
        localStorage.setItem('token', this.token);
        localStorage.setItem('user-data', JSON.stringify(this.user));
        this.updateLastActivity();
      }
    },

    // Nettoyer le localStorage
    clearStorage() {
      localStorage.removeItem('token');
      localStorage.removeItem('user-data');
      localStorage.removeItem('last-activity');
      localStorage.removeItem('user-role');
    },

    clearPendingTwoFactor() {
      this.pendingTwoFactorToken = null;
    },

    // Mettre à jour la dernière activité
    updateLastActivity() {
      this.lastActivity = Date.now();
      localStorage.setItem('last-activity', this.lastActivity.toString());
    },

    /**
     * @returns true si connecté, false si erreur, 'needs-2fa' si code TOTP requis
     */
    async login(email: string, password: string): Promise<boolean | 'needs-2fa'> {
      this.isLoading = true;
      this.error = null;
      this.pendingTwoFactorToken = null;

      try {
        if (!email || !password) {
          throw new Error('Email et mot de passe sont obligatoires');
        }

        const response = await authService.login(email, password);

        if (response.success && response.data) {
          const data = response.data as {
            needsTwoFactor?: boolean;
            twoFactorToken?: string;
            user?: User;
            tokens?: { accessToken?: string; access?: string; token?: string };
          };

          if (data.needsTwoFactor && data.twoFactorToken) {
            this.pendingTwoFactorToken = data.twoFactorToken;
            return 'needs-2fa';
          }

          const { user, tokens } = data;
          const token =
            tokens?.accessToken || tokens?.access || tokens?.token || (tokens as unknown as string);

          if (token && user) {
            this.token = typeof token === 'string' ? token : String(token);
            this.user = user;
            this.saveToStorage();
            if (user.role) {
              localStorage.setItem('user-role', user.role as string);
            }
            return true;
          }
          throw new Error('Données de connexion incomplètes');
        }
        throw new Error(response.message || 'Échec de la connexion');
      } catch (error: any) {
        this.error = error.message || 'Échec de la connexion';
        console.error('Erreur de connexion:', error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /** Finalise la connexion après le code 2FA (utiliser pendingTwoFactorToken) */
    async loginTwoFactor(code: string): Promise<boolean> {
      const token = this.pendingTwoFactorToken;
      if (!token) {
        this.error = 'Session 2FA expirée, reconnectez-vous.';
        return false;
      }
      this.isLoading = true;
      this.error = null;
      try {
        const response = await authService.loginTwoFactor(token, code);
        if (response.success && response.data && 'user' in response.data) {
          const { user, tokens } = response.data;
          const access =
            tokens?.accessToken || tokens?.access || tokens?.token || (tokens as unknown as string);
          if (access && user) {
            this.token = typeof access === 'string' ? access : String(access);
            this.user = user;
            this.pendingTwoFactorToken = null;
            this.saveToStorage();
            if (user.role) {
              localStorage.setItem('user-role', user.role as string);
            }
            return true;
          }
        }
        throw new Error(response.message || 'Échec de la connexion');
      } catch (error: any) {
        this.error = error.message || 'Code incorrect';
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    
    async logout() {
      try {
        // Appel API pour déconnecter l'utilisateur (si nécessaire)
        if (this.token) {
          try {
            await authService.logout();
          } catch (error) {
            console.warn('Erreur lors de la déconnexion côté serveur:', error);
          }
        }
        
        // Nettoyer le state et le localStorage
        this.clearStorage();
        this.token = null;
        this.user = null;
        this.error = null;
        this.lastActivity = null;
        this.pendingTwoFactorToken = null;
        
        // IMPORTANT: Garder isInitialized à true même après déconnexion
        // pour permettre la navigation pour les utilisateurs non connectés
        // Le router guard attend que isInitialized soit true pour continuer
        this.isInitialized = true;
        
        return true;
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
        // Même en cas d'erreur, s'assurer que isInitialized reste true
        this.isInitialized = true;
        return false;
      }
    },
    
    async fetchCurrentUser() {
      if (!this.token || this.isTokenExpired) {
        if (this.isTokenExpired) {
          console.log('⚠️ Token expiré lors de fetchCurrentUser');
          await this.logout();
        }
        return false;
      }
      
      // Éviter les vérifications multiples
      if (this.isCheckingAuth) {
        console.log('⏳ Vérification déjà en cours...');
        return false;
      }
      
      this.isCheckingAuth = true;
      this.isLoading = true;
      
      try {
        console.log('🔄 Récupération des informations utilisateur...');
        const response = await authService.getCurrentUser();
        
        if (response.success) {
          const user = response.data?.user;
          if (!user) {
            throw new Error('Données utilisateur manquantes dans la réponse');
          }
          
          this.user = user;
          
          // Mettre à jour le localStorage
          this.saveToStorage();
          
          console.log('✅ Utilisateur récupéré avec succès:', {
            userId: user._id || user.id,
            email: user.email,
            role: user.role
          });
          
          return true;
        } else {
          throw new Error(response.message || 'Erreur lors de la récupération du profil');
        }
      } catch (error: any) {
        console.error('❌ Erreur lors de la récupération du profil:', error);
        
        // Si erreur 401, déconnecter l'utilisateur
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          console.log('🚫 Erreur 401, déconnexion...');
          await this.logout();
        }
        
        return false;
      } finally {
        this.isLoading = false;
        this.isCheckingAuth = false;
      }
    },

    async register(userData: any) {
      this.isLoading = true;
      this.error = null;
      
      try {
        if (!userData.firstName || !userData.lastName || !userData.email || !userData.password) {
          throw new Error('Tous les champs sont obligatoires');
        }

        if (userData.password.length < 8) {
          throw new Error('Le mot de passe doit contenir au moins 8 caractères');
        }

        const response = await authService.signup(userData);
        
        if (response.success && response.data?.tokens?.accessToken && response.data.user) {
          const { tokens, user: signedUp } = response.data;
          this.token = tokens.accessToken!;
          this.user = signedUp as User;
          this.saveToStorage();

          if (signedUp.role) {
            localStorage.setItem('user-role', signedUp.role as string);
          }
        }
        
        return response;
      } catch (error: any) {
        this.error =
          error?.name === 'AuthApiError' && error?.message
            ? error.message
            : error?.message || 'Échec de l\'inscription';
        console.error('Erreur d\'inscription:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Vérifier si le token est valide
    isTokenValid() {
      return !!this.token && !this.isTokenExpired;
    },

    // Nettoyer les erreurs
    clearError() {
      this.error = null;
    },

    // Initialiser l'authentification au démarrage de l'app
    async initializeAuth() {
      if (this.isInitialized) return;
      
      console.log('🔐 Initialisation de l\'authentification...');

      // Écouter les événements émis par l'intercepteur api.ts
      window.addEventListener('token-refreshed', (e: Event) => {
        const newToken = (e as CustomEvent<{ token: string }>).detail.token;
        this.token = newToken;
        this.saveToStorage();
        console.log('🔄 Token mis à jour depuis l\'intercepteur');
      });

      window.addEventListener('auth-session-expired', async () => {
        console.log('🚫 Session expirée, déconnexion forcée...');
        await this.logout();
      });
      
      try {
        // Essayer de récupérer depuis le localStorage
        if (this.initializeFromStorage()) {
          console.log('✅ Authentification restaurée depuis le localStorage');
          
          // Vérifier que le token est toujours valide côté serveur
          if (this.token && !this.isTokenExpired) {
            try {
              await this.fetchCurrentUser();
              console.log('✅ Utilisateur récupéré avec succès côté serveur');
            } catch (error) {
              console.warn('⚠️ Erreur lors de la récupération côté serveur:', error);
              // Si la récupération échoue, nettoyer l'état
              this.clearStorage();
            }
          } else {
            console.log('⚠️ Token expiré, déconnexion...');
            await this.logout();
          }
        } else {
          console.log('🔑 Aucune authentification trouvée');
        }
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation:', error);
        this.clearStorage();
      } finally {
        // Toujours marquer comme initialisé, même en cas d'erreur
        this.isInitialized = true;
        console.log('✅ Authentification initialisée, état final:', {
          isAuthenticated: this.isAuthenticated,
          hasToken: !!this.token,
          hasUser: !!this.user
        });
      }
    },

    // Vérifier et restaurer l'authentification si nécessaire
    async checkAndRestoreAuth() {
      // Si on a un token mais pas d'utilisateur, essayer de le récupérer
      if (this.token && !this.user && !this.isCheckingAuth && !this.isTokenExpired) {
        console.log('🔄 Restauration de l\'authentification...');
        await this.fetchCurrentUser();
      }
    },

    // Synchroniser l'état d'authentification
    syncAuthState() {
      console.log('🔄 Synchronisation de l\'état d\'authentification...');
      console.log('État actuel:', {
        hasToken: !!this.token,
        hasUser: !!this.user,
        isAuthenticated: this.isAuthenticated,
        isTokenExpired: this.isTokenExpired
      });
      
      // Si le token est expiré, nettoyer immédiatement
      if (this.token && this.isTokenExpired) {
        console.log('⚠️ Token expiré détecté, nettoyage immédiat...');
        this.clearStorage();
        this.token = null;
        this.user = null;
        return;
      }
      
      // Si on a un token mais pas d'utilisateur, essayer de restaurer
      if (this.token && !this.user && !this.isTokenExpired) {
        console.log('🔄 Tentative de restauration de l\'utilisateur...');
        this.fetchCurrentUser().catch(error => {
          console.warn('⚠️ Échec de la restauration:', error);
          this.clearStorage();
        });
      }
    },

    // Rafraîchir le token si nécessaire
    async refreshTokenIfNeeded() {
      if (this.token && this.isTokenExpired) {
        console.log('🔄 Token expiré, tentative de rafraîchissement...');
        // Ici vous pourriez implémenter une logique de refresh token
        // Pour l'instant, on déconnecte l'utilisateur
        await this.logout();
        return false;
      }
      return true;
    },

    // Vérifier l'expiration d'un token spécifique (utilisé pour l'initialisation)
    checkTokenExpiration(token: string): boolean {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000; // Convertir en millisecondes
        const isExpired = Date.now() >= expiry;
        
        console.log('🔍 Vérification d\'expiration du token:', {
          expiry: new Date(expiry).toISOString(),
          now: new Date().toISOString(),
          isExpired
        });
        
        return isExpired;
      } catch (error) {
        console.error('❌ Erreur lors de la vérification d\'expiration du token:', error);
        return true; // Considérer comme expiré en cas d'erreur
      }
    }
  }
}); 